/**
 * Resource Node Renderer
 * 
 * Handles rendering of all gathering resource nodes (fishing spots, mining, foraging, salvaging)
 * Uses sprite-based rendering for visual fidelity
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';

export interface NodeRenderConfig {
  nodeType: string;
  tileX: number;
  tileY: number;
  phase: number;
  isActive?: boolean;
}

// Map node types to sprite paths and fallback colors
export const RESOURCE_NODE_SPRITES: Record<string, { path: string; fallbackColor: number }> = {
  // Salvaging nodes
  shipwreck_debris: { path: '/assets/tilesets/resources/salvaging/shipwreck_debris.png', fallbackColor: 0x8b4513 },
  sunken_rowboat: { path: '/assets/tilesets/resources/salvaging/sunken_rowboat.png', fallbackColor: 0x6b4423 },
  merchant_wreck: { path: '/assets/tilesets/resources/salvaging/merchant_wreck.png', fallbackColor: 0xb8860b },
  abandoned_cargo: { path: '/assets/tilesets/resources/salvaging/abandoned_cargo.png', fallbackColor: 0xdaa520 },
  naval_wreck: { path: '/assets/tilesets/resources/salvaging/naval_wreck.png', fallbackColor: 0x4682b4 },
  ancient_ruins: { path: '/assets/tilesets/resources/salvaging/ancient_ruins.png', fallbackColor: 0x9370db },
  // Mining nodes
  stone_quarry: { path: '/assets/tilesets/resources/mining/stone_quarry.png', fallbackColor: 0x696969 },
  tin_deposit: { path: '/assets/tilesets/resources/mining/tin_deposit.png', fallbackColor: 0xa9a9a9 },
  mineral_outcrop: { path: '/assets/tilesets/resources/mining/mineral_outcrop.png', fallbackColor: 0x708090 },
  silver_vein: { path: '/assets/tilesets/resources/mining/silver_vein.png', fallbackColor: 0xc0c0c0 },
  gold_deposit: { path: '/assets/tilesets/resources/mining/gold_deposit.png', fallbackColor: 0xffd700 },
  coal_seam: { path: '/assets/tilesets/resources/mining/coal_seam.png', fallbackColor: 0x2f2f2f },
  volcanic_vent: { path: '/assets/tilesets/resources/mining/volcanic_vent.png', fallbackColor: 0xff4500 },
  // Foraging nodes
  driftwood_cluster: { path: '/assets/tilesets/resources/foraging/driftwood_cluster.png', fallbackColor: 0xcd853f },
  coconut_palm: { path: '/assets/tilesets/resources/foraging/coconut_palm.png', fallbackColor: 0x228b22 },
  seaweed_bed: { path: '/assets/tilesets/resources/foraging/seaweed_bed.png', fallbackColor: 0x2e8b57 },
  herb_patch: { path: '/assets/tilesets/resources/foraging/herb_patch.png', fallbackColor: 0x32cd32 },
  fruit_grove: { path: '/assets/tilesets/resources/foraging/fruit_grove.png', fallbackColor: 0xff6347 },
  mushroom_colony: { path: '/assets/tilesets/resources/foraging/mushroom_colony.png', fallbackColor: 0x8b4513 },
  rare_flower_bed: { path: '/assets/tilesets/resources/foraging/rare_flower_bed.png', fallbackColor: 0xff69b4 },
  // Fishing nodes
  shallow_waters: { path: '/assets/tilesets/resources/fishing/shallow_waters.png', fallbackColor: 0x87ceeb },
  deep_sea: { path: '/assets/tilesets/resources/fishing/deep_sea.png', fallbackColor: 0x1e3a5f },
  coral_reef: { path: '/assets/tilesets/resources/fishing/coral_reef.png', fallbackColor: 0x20b2aa },
  fishing_hole: { path: '/assets/tilesets/resources/fishing/fishing_hole.png', fallbackColor: 0x4169e1 },
  shipwreck_pool: { path: '/assets/tilesets/resources/fishing/shipwreck_pool.png', fallbackColor: 0x2c3e50 },
  volcanic_waters: { path: '/assets/tilesets/resources/fishing/volcanic_waters.png', fallbackColor: 0xff4500 },
  abyssal_trench: { path: '/assets/tilesets/resources/fishing/abyssal_trench.png', fallbackColor: 0x0a0e27 },
  whirlpool_edge: { path: '/assets/tilesets/resources/fishing/whirlpool_edge.png', fallbackColor: 0x1e90ff },
  moonlit_shallows: { path: '/assets/tilesets/resources/fishing/moonlit_shallows.png', fallbackColor: 0xb0e0e6 },
  ancient_grounds: { path: '/assets/tilesets/resources/fishing/ancient_grounds.png', fallbackColor: 0x483d8b },
};

export const getResourceNodeSpriteKeys = (): Array<string> => Object.keys(RESOURCE_NODE_SPRITES);

export class ResourceNodeRenderer {
  /**
   * Create a sprite-based resource node (80% of tile size)
   */
  static createNodeSprite(scene: Phaser.Scene, nodeType: string, tileX: number, tileY: number, isActive: boolean = false): Phaser.GameObjects.Container {
    const x = tileX * TILE_SIZE + TILE_SIZE / 2;
    const y = tileY * TILE_SIZE + TILE_SIZE / 2;
    
    const container = scene.add.container(x, y);
    
    const spriteConfig = RESOURCE_NODE_SPRITES[nodeType];
    const fallbackColor = spriteConfig?.fallbackColor || 0x808080;
    
    // Scale sprites to 80% of tile size
    const spriteSize = TILE_SIZE * 0.8;
    
    let spriteAdded: boolean = false;

    // Try to create sprite, fall back to graphics if sprite not available
    if (spriteConfig && scene.textures.exists(nodeType)) {
      try {
        const sprite = scene.add.image(0, 0, nodeType);
        sprite.setDisplaySize(spriteSize, spriteSize);
        container.add(sprite);
        spriteAdded = true;
      } catch (err) {
        console.warn(`⚠️ Failed to create sprite for ${nodeType}`);
        ResourceNodeRenderer.createFallbackGraphics(scene, container, nodeType, fallbackColor, spriteSize);
      }
    } else {
      if (spriteConfig && !scene.textures.exists(nodeType)) {
        console.warn(`⚠️ Missing texture for resource node: ${nodeType}`);
      }
      // Use fallback graphics
      ResourceNodeRenderer.createFallbackGraphics(scene, container, nodeType, fallbackColor, spriteSize);

      if (spriteConfig) {
        const handleTextureAdd = (textureKey: string): void => {
          if (textureKey !== nodeType) {
            return;
          }
          container.list
            .filter((child) => child.name === 'resource-node-fallback')
            .forEach((child) => child.destroy());
          try {
            const sprite = scene.add.image(0, 0, nodeType);
            sprite.setDisplaySize(spriteSize, spriteSize);
            container.addAt(sprite, 0);
            ResourceNodeRenderer.addSpriteOverlay(container, fallbackColor, spriteSize);
          } catch (err) {
            console.warn(`⚠️ Failed to swap sprite for ${nodeType}`);
          }
          scene.textures.off(Phaser.Textures.Events.ADD, handleTextureAdd);
        };

        scene.textures.on(Phaser.Textures.Events.ADD, handleTextureAdd);
      }
    }
    
    if (spriteAdded) {
      ResourceNodeRenderer.addSpriteOverlay(container, fallbackColor, spriteSize);
    }

    // Add active border if needed
    if (isActive) {
      const border = scene.add.rectangle(0, 0, spriteSize + 2, spriteSize + 2);
      border.setStrokeStyle(3, 0xffff00);
      container.add(border);
    }

    return container;
  }

  /**
   * Create fallback graphics when sprite is unavailable
   */
  private static createFallbackGraphics(scene: Phaser.Scene, container: Phaser.GameObjects.Container, nodeType: string, color: number, size: number = TILE_SIZE): void {
    const graphics = scene.add.graphics();
    graphics.setName('resource-node-fallback');
    graphics.fillStyle(color, 0.5);
    graphics.fillRect(-size / 2, -size / 2, size, size);
    graphics.lineStyle(1, color, 0.8);
    graphics.strokeRect(-size / 2, -size / 2, size, size);
    container.add(graphics);
  }

  private static addSpriteOverlay(container: Phaser.GameObjects.Container, color: number, size: number): void {
    const overlay = container.scene.add.graphics();
    overlay.setName('resource-node-overlay');
    
    // Outer glow effect
    overlay.lineStyle(2, color, 0.3);
    overlay.strokeRect(-size / 2 - 1, -size / 2 - 1, size + 2, size + 2);
    
    // Main border with color
    overlay.lineStyle(1.5, color, 0.8);
    overlay.strokeRect(-size / 2, -size / 2, size, size);
    
    // Inner highlight accent
    overlay.lineStyle(1, color, 0.5);
    overlay.strokeRect(-size / 2 + 1, -size / 2 + 1, size - 2, size - 2);
    
    container.add(overlay);
  }

  /**
   * Draw a resource node with pixel art style (legacy graphics-based method)
   * Kept for backward compatibility with existing code
   */
  static drawNode(graphics: Phaser.GameObjects.Graphics, nodeType: string, tileX: number, tileY: number, phase: number, isActive: boolean = false): void {
    const x = tileX * TILE_SIZE;
    const y = tileY * TILE_SIZE;
    const spriteConfig = RESOURCE_NODE_SPRITES[nodeType];
    const color = spriteConfig?.fallbackColor || 0x808080;

    // Base tile
    graphics.fillStyle(color, 0.5);
    graphics.fillRect(x, y, TILE_SIZE, TILE_SIZE);

    // Draw specific node type
    ResourceNodeRenderer.drawNodePattern(graphics, nodeType, x, y, phase);

    // Border - bright highlight if active
    if (isActive) {
      graphics.lineStyle(3, 0xffff00, 1);
      graphics.strokeRect(x - 1, y - 1, TILE_SIZE + 2, TILE_SIZE + 2);
      graphics.lineStyle(1, 0xffaa00, 0.8);
      graphics.strokeRect(x, y, TILE_SIZE, TILE_SIZE);
    } else {
      graphics.lineStyle(1, color, 0.8);
      graphics.strokeRect(x, y, TILE_SIZE, TILE_SIZE);
    }
  }

  private static drawNodePattern(graphics: Phaser.GameObjects.Graphics, nodeType: string, x: number, y: number, phase: number): void {
    switch (nodeType) {
      case 'shipwreck_debris':
        graphics.fillStyle(0x654321, 0.9);
        graphics.fillRect(x + 2, y + 4, 6, 2);
        graphics.fillRect(x + 8, y + 8, 5, 2);
        graphics.fillRect(x + 3, y + 11, 7, 2);
        graphics.fillStyle(0x8b4513, 0.8);
        graphics.fillRect(x + 6, y + 2, 4, 3);
        const bubbleY1 = y + 6 - (phase % 2);
        graphics.fillStyle(0xffffff, 0.6);
        graphics.fillRect(x + 12, bubbleY1, 2, 2);
        break;

      case 'stone_quarry':
        graphics.fillStyle(0x4a4a4a, 0.9);
        graphics.fillRect(x + 3, y + 9, 4, 4);
        graphics.fillRect(x + 9, y + 7, 3, 3);
        graphics.fillStyle(0xb87333, 0.85);
        graphics.fillRect(x + 6, y + 6, 1, 1);
        graphics.fillRect(x + 11, y + 11, 1, 1);
        if (phase % 2 === 0) {
          graphics.fillStyle(0xffffff, 0.7);
          graphics.fillRect(x + 5, y + 5, 1, 1);
        }
        break;

      case 'mineral_outcrop':
        graphics.fillStyle(0x4a4a4a, 0.9);
        graphics.fillRect(x + 4, y + 8, 8, 6);
        graphics.fillStyle(0x696969, 0.9);
        graphics.fillRect(x + 6, y + 4, 4, 6);
        graphics.fillStyle(0x87ceeb, 0.8);
        graphics.fillRect(x + 7, y + 5, 2, 3);
        if (phase % 2 === 0) {
          graphics.fillStyle(0xffffff, 0.9);
          graphics.fillRect(x + 8, y + 3, 1, 1);
        }
        break;

      case 'driftwood_cluster':
        graphics.fillStyle(0x8b7355, 0.9);
        graphics.fillRect(x + 1, y + 5, 10, 3);
        graphics.fillRect(x + 5, y + 9, 8, 3);
        graphics.fillStyle(0xa0522d, 0.8);
        graphics.fillRect(x + 3, y + 2, 6, 2);
        const rippleX = x + 2 + phase;
        graphics.fillStyle(0x4169e1, 0.4);
        graphics.fillRect(rippleX, y + 13, 3, 1);
        break;

      case 'abandoned_cargo':
        graphics.fillStyle(0xb8860b, 0.9);
        graphics.fillRect(x + 3, y + 4, 10, 9);
        graphics.fillStyle(0x8b6914, 0.9);
        graphics.fillRect(x + 3, y + 7, 10, 2);
        graphics.fillRect(x + 7, y + 4, 2, 9);
        graphics.fillStyle(0xffd700, 0.7);
        graphics.fillRect(x + 7, y + 7, 2, 2);
        if (phase % 2 === 1) {
          graphics.fillStyle(0x4169e1, 0.3);
          graphics.fillRect(x + 2, y + 13, 12, 1);
        }
        break;

      case 'seaweed_bed':
        const waveOffset = phase % 2;
        graphics.fillStyle(0x228b22, 0.8);
        graphics.fillRect(x + 2 + waveOffset, y + 4, 2, 10);
        graphics.fillRect(x + 6 - waveOffset, y + 6, 2, 8);
        graphics.fillRect(x + 10 + waveOffset, y + 3, 2, 11);
        graphics.fillStyle(0x32cd32, 0.7);
        graphics.fillRect(x + 4, y + 8 + waveOffset, 2, 6);
        graphics.fillRect(x + 12, y + 7 - waveOffset, 2, 7);
        break;

      case 'ancient_ruins':
        graphics.fillStyle(0x696969, 0.9);
        graphics.fillRect(x + 2, y + 6, 4, 8);
        graphics.fillRect(x + 10, y + 4, 4, 10);
        graphics.fillStyle(0x808080, 0.8);
        graphics.fillRect(x + 5, y + 9, 6, 2);
        graphics.fillStyle(0x9370db, 0.6);
        graphics.fillRect(x + 3, y + 7, 2, 2);
        if (phase % 2 === 0) {
          graphics.fillStyle(0xdda0dd, 0.5);
          graphics.fillRect(x + 11, y + 5, 2, 2);
        }
        break;

      case 'volcanic_vent':
        graphics.fillStyle(0x2f4f4f, 0.9);
        graphics.fillRect(x + 4, y + 8, 8, 6);
        graphics.fillStyle(0xff4500, 0.7);
        graphics.fillRect(x + 6, y + 6, 4, 4);
        const bubble1Y = y + 4 - phase;
        const bubble2Y = y + 2 - ((phase + 2) % 4);
        graphics.fillStyle(0xff6347, 0.8);
        graphics.fillRect(x + 7, bubble1Y, 2, 2);
        graphics.fillStyle(0xffa500, 0.6);
        graphics.fillRect(x + 10, bubble2Y, 2, 2);
        graphics.fillStyle(0xdcdcdc, 0.4);
        graphics.fillRect(x + 5, y + 1 + (phase % 2), 2, 2);
        break;

      case 'coconut_palm':
        // Clean, grid-aligned palm: slim trunk + three fronds + coconuts.
        graphics.fillStyle(0x6b4423, 0.95);
        graphics.fillRect(x + 7, y + 5, 2, 10);
        graphics.fillStyle(0x8b6914, 0.9);
        graphics.fillRect(x + 7, y + 7, 2, 1);
        graphics.fillRect(x + 7, y + 10, 2, 1);

        // Fronds (blocky)
        graphics.fillStyle(0x1f7a1f, 0.9);
        graphics.fillRect(x + 2, y + 3, 6, 2);
        graphics.fillRect(x + 8, y + 3, 6, 2);
        graphics.fillRect(x + 5, y + 1, 6, 2);
        graphics.fillStyle(0x228b22, 0.9);
        graphics.fillRect(x + 4, y + 4, 3, 1);
        graphics.fillRect(x + 9, y + 4, 3, 1);

        // Coconuts (tiny squares)
        graphics.fillStyle(0x8b4513, 0.9);
        graphics.fillRect(x + 6, y + 5, 2, 2);
        graphics.fillRect(x + 8, y + 5, 2, 2);
        break;

      case 'herb_patch':
        graphics.fillStyle(0x228b22, 0.8);
        graphics.fillRect(x + 2, y + 8, 3, 6);
        graphics.fillRect(x + 7, y + 7, 3, 7);
        graphics.fillRect(x + 11, y + 9, 3, 5);
        graphics.fillStyle(0x32cd32, 0.7);
        graphics.fillRect(x + 4, y + 6, 4, 4);
        graphics.fillStyle(0x90ee90, 0.6);
        graphics.fillRect(x + 5, y + 7, 2, 2);
        break;

      case 'fruit_grove':
        // Two small trees with a few fruit pixels (less blobby, more readable)
        graphics.fillStyle(0x6b4423, 0.95);
        graphics.fillRect(x + 4, y + 10, 2, 6);
        graphics.fillRect(x + 10, y + 10, 2, 6);

        graphics.fillStyle(0x228b22, 0.9);
        graphics.fillRect(x + 2, y + 4, 6, 6);
        graphics.fillRect(x + 8, y + 4, 6, 6);
        graphics.fillStyle(0x1f7a1f, 0.9);
        graphics.fillRect(x + 3, y + 3, 4, 2);
        graphics.fillRect(x + 9, y + 3, 4, 2);

        // Fruit (tiny, grid-aligned)
        const fruitBlink: number = phase % 2;
        graphics.fillStyle(0xff6347, 0.95);
        graphics.fillRect(x + 4, y + 6, 1, 1);
        graphics.fillRect(x + 6, y + 7, 1, 1);
        graphics.fillRect(x + 10, y + 6, 1, 1);
        if (fruitBlink === 0) {
          graphics.fillStyle(0xffd700, 0.85);
          graphics.fillRect(x + 12, y + 7, 1, 1);
        }
        break;

      case 'gold_deposit':
        // Rock base + gold flecks (avoid big bright block)
        graphics.fillStyle(0x4a4a4a, 0.95);
        graphics.fillRect(x + 3, y + 7, 10, 7);
        graphics.fillStyle(0x696969, 0.9);
        graphics.fillRect(x + 5, y + 5, 6, 5);
        graphics.fillStyle(0xffd700, 0.95);
        graphics.fillRect(x + 6, y + 8, 1, 1);
        graphics.fillRect(x + 9, y + 9, 1, 1);
        graphics.fillRect(x + 8, y + 11, 1, 1);
        if (phase % 2 === 0) {
          graphics.fillStyle(0xffffff, 0.75);
          graphics.fillRect(x + 10, y + 7, 1, 1);
        }
        break;

      case 'mushroom_colony':
        graphics.fillStyle(0xf5deb3, 0.9);
        graphics.fillRect(x + 3, y + 10, 2, 4);
        graphics.fillRect(x + 8, y + 9, 2, 5);
        graphics.fillRect(x + 12, y + 11, 2, 3);
        graphics.fillStyle(0x8b4513, 0.85);
        graphics.fillRect(x + 1, y + 7, 6, 4);
        graphics.fillRect(x + 6, y + 5, 6, 5);
        graphics.fillRect(x + 10, y + 8, 5, 4);
        break;

      case 'rare_flower_bed':
        // Compact bed: stems + three small blossoms (plus-shapes)
        graphics.fillStyle(0x1f7a1f, 0.9);
        graphics.fillRect(x + 4, y + 10, 1, 5);
        graphics.fillRect(x + 8, y + 9, 1, 6);
        graphics.fillRect(x + 12, y + 10, 1, 5);

        const blink: number = phase % 2;
        graphics.fillStyle(0xff69b4, 0.95);
        // Left flower
        graphics.fillRect(x + 3, y + 8, 3, 1);
        graphics.fillRect(x + 4, y + 7, 1, 3);
        // Center flower
        graphics.fillRect(x + 7, y + 7, 3, 1);
        graphics.fillRect(x + 8, y + 6, 1, 3);
        // Right flower
        graphics.fillRect(x + 11, y + 8, 3, 1);
        graphics.fillRect(x + 12, y + 7, 1, 3);

        // Highlights
        graphics.fillStyle(0xffffff, 0.75);
        graphics.fillRect(x + 4, y + 8, 1, 1);
        if (blink === 0) {
          graphics.fillRect(x + 8, y + 7, 1, 1);
        }
        break;

      // High-level nodes (abbreviated for brevity - full implementations in OceanScene)
      case 'pirate_cache':
      case 'sunken_temple':
      case 'ghost_ship':
      case 'titan_remains':
      case 'void_rift_debris':
      case 'lost_vault':
      case 'astral_ore_deposit':
      case 'dusksteel_vein':
      case 'sigilstone_deposit':
      case 'wyrmstone_ore':
      case 'void_crystal':
      case 'primordial_shard':
      case 'ancient_tree':
      case 'crystal_garden':
      case 'bioluminescent_grove':
      case 'elder_growth':
      case 'world_tree_sapling':
      case 'void_bloom':
      case 'world_root_fragment':
      case 'whirlpool_edge':
      case 'moonlit_shallows':
      case 'ancient_grounds':
        // Render as simple colored rectangles for high-level nodes
        const spriteConfig = RESOURCE_NODE_SPRITES[nodeType];
        const fallbackColor = spriteConfig?.fallbackColor || 0x808080;
        graphics.fillStyle(fallbackColor, 0.7);
        graphics.fillRect(x + 4, y + 4, 8, 8);
        break;

      default:
        // Generic resource
        const defaultSpriteConfig = RESOURCE_NODE_SPRITES[nodeType];
        const defaultFallbackColor = defaultSpriteConfig?.fallbackColor || 0x808080;
        graphics.fillStyle(defaultFallbackColor, 0.7);
        graphics.fillRect(x + 4, y + 4, 8, 8);
    }
  }
}
